import { brandingInfo } from "features/server/brandingSlice";
import { Badge, Box, Button, CircularProgress, Fade, Grid, Popover, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { themes } from "theme/themes";
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetNotificationsService } from "subModule/src/services/Notifications/useNotification";
import { selectUserInfo } from "features/user/userSlice";
import { GetNotificationResponse } from "subModule/src/services/Notifications/interface";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DynamicSelect from "common/Select/Select";
import NotificationItem from "./NotifcationItem";
import { actionTypesInfo } from "features/user/actionSlice";
import { NotificationProfileService } from "subModule/src/core/services";
import { Wrapper } from "subModule/src/type";
import { CheckUnreadCount } from "Helpers/CheckUnreadCount";
import { setUnreadCount } from "features/user/notificationSlice";
import { useNavigate } from "react-router-dom";
import { SettingsOutlined } from "@mui/icons-material";
export default function Notifications() {
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDetails = useSelector(selectUserInfo);
    const alertActions = useSelector(actionTypesInfo);
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [data, setData] = useState<any>({});
    const [pageNo, setPageNo] = useState<number>(1);
    const [notificationType, setNotificationType] = useState<number | null>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const unreadCount = useSelector((state:any) => state.notifications.unreadCount);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const { isFetching, refetch } = 
        useGetNotificationsService({ pageNumber: pageNo, size: 50, userId: userDetails?.id, type: notificationType });
    useEffect(() => {
        if(anchorEl) fetchData();
    }, [notificationType, pageNo]);
    const fetchData = useCallback(() => {
        refetch().then((res: GetNotificationResponse | any) => {
            if(res?.data?.pages[0]?.success) {
                setData(res?.data?.pages[0]?.data || {});
            } else {
                enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
            }
        }).finally(() => {
            CheckUnreadCount(userDetails?.id || 0, dispatch, enqueueSnackbar);
            setLoading(false);
        });
    }, []);
    const notificationTypes = [
        { value: 100, label: t('jobCard') },
        { value: 151, label: t('estimation') },
        { value: 200, label: t('workOrder') },
        { value: 300, label: t('invoice') },
        { value: 400, label: t('payment') },
        { value: 500, label: t('creditNote') },
        { value: 600, label: t('cart') },
        { value: 700, label: t('inspection') },
        { value: 800, label: t('RETURNCASH') },
        { value: 900, label: t('SOS') },
        { value: 1000, label: t('Purchase Order') },
        { value: 1100, label: t('purchaseBill') },
        { value: 1200, label: t('PURCHASE_PAYMENT') },
        { value: 1300, label: t('Stock Received') },
        { value: 1400, label: t('StockTransfer') },
        { value: 1500, label: t('Unstock') },
        { value: 1600, label: t('PURCHASE_CREDIT_DEBIT_NOTE') },
        { value: 1700, label: t('StockAdjustment') },
        { value: 1800, label: t('Inventory') },
        { value: 2000, label: t('BOOKING') },
    ];
    const isHiddenRolePrivilegeNotification = (n: any) => {
        const typeKey = String(alertActions?.types?.[n?.type] || "")
            .toUpperCase()
            .replace(/\s+/g, "_");
        if (typeKey !== "ROLE_PRIVILEGE") return false;
        const actionKey = String(alertActions?.actions?.[n?.action] || "").toUpperCase();
        return (
            !actionKey ||
            actionKey.includes("ASSIGN") ||
            actionKey.includes("UNASSIGN") ||
            [83, 84, 85].includes(Number(n?.action))
        );
    };
    // Derived directly from loaded notification objects — no secondary API call needed.
    // Falls back to Redux unreadCount only before the panel data has been fetched.
    const hasUnread = data?.content != null
        ? (data.content as any[]).some((n: any) => !n.read && !isHiddenRolePrivilegeNotification(n))
        : unreadCount > 0;
    const handleAllRead = () => {
        if (!hasUnread) return;
        setLoading(true);
        NotificationProfileService.markAllRead({}).then((res: Wrapper) => {
            if(res.success) {
                // Optimistic update: hide the button and clear the badge immediately,
                // without waiting for the background refetch to complete.
                setData((prev: any) => ({
                    ...prev,
                    content: (prev?.content ?? []).map((n: any) => ({ ...n, read: true })),
                }));
                dispatch(setUnreadCount(0));
                fetchData();
            } else {
                enqueueSnackbar(<AlertMessages text={res?.message || "Something went wrong"} />, { variant: "error" });
                setLoading(false);
            }
        }).catch((err: any) => {
            console.error("===", err);
            setLoading(false);
        });
    }
    const handleReadUnread = (row: any) => {
        setLoading(true);
        NotificationProfileService.markReadUnread({ id: row.id, typeName: row?.read ? 'alerts/unread' : 'alerts/read'}).then((res: Wrapper) => {
            if(res.success) {
                fetchData();
            } else enqueueSnackbar(<AlertMessages text={res?.message || "Something went wrong"} />, { variant: "error" });
        }).catch((err: any) => {
            console.error("===", err);
        });
    }
    const closePopover = () => {
        setAnchorEl(null);
        setNotificationType(0);
        setPageNo(1);
    }
    return (<>
        <Box 
            aria-describedby={open ? 'simple-popover' : undefined} 
            onClick={(event: React.MouseEvent<HTMLElement>) => {
                setAnchorEl(event.currentTarget);
                fetchData();
            }}
        >
            <Badge
                badgeContent={unreadCount}
                color="error" max={99}
                sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontFamily: `'Saira', sans-serif` } }}
                componentsProps={{
                    badge: { title: unreadCount?.toLocaleString(), },
                }}
            >
                <Tooltip title={t("notifications")}>
                    <Box
                        style={{ background: themeColors?.button, color: themeColors?.primary }}
                        className='rounded-[10px] flex items-center px-[8px] h-10 cursor-pointer relative'
                    ><NotificationsOutlinedIcon /></Box>
                </Tooltip>
            </Badge>
        </Box>
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={closePopover}
            TransitionComponent={Fade}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            disableScrollLock
            sx={{ mt: '5px', 
                '& .MuiPaper-root': { 
                    width: { xs: '100%', md: '500px' },
                    borderRadius: '8px', overflowY: 'hidden'
                },
            }}
        >
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Box className='flex items-center justify-between gap-1 p-[15px] border-b-[#f0f0f0] border-b'>
                        <Typography className={`font-Saira text-[15px] font-semibold`}>{t('notifications')}</Typography>
                        <Box className='flex items-center gap-2'>
                            {hasUnread && (
                                <Button 
                                    variant="text"
                                    disabled={loading}
                                    className={`font-Saira text-[13px] font-[300] underline cursor-pointer p-0 normal-case`}
                                    sx={{ color: loading ? '#B8C0C6' : themeColors?.primary, background: 'transparent !important' }}
                                    onClick={handleAllRead}
                                >{t('markAllAsRead')}</Button>
                            )}
                            <Box sx={{ 
                                '& .dropdown': { display: 'none !important' },
                                '& .css-vbvrjr-placeholder': { fontSize: '13px !important' },
                                width: '180px',
                            }}>
                                <DynamicSelect
                                    label=''
                                    isClearable
                                    isSearchable
                                    options={notificationTypes}
                                    onChange={(option) => {
                                        setNotificationType(option?.value || null);
                                        setPageNo(1);
                                    }}
                                    defaultPlaceholder={t('select') + ' ' + t('type')}
                                    isDisabled={isFetching}
                                />
                            </Box>
                            <Tooltip title={t("Notification Settings")} followCursor>
                                <SettingsOutlined className="cursor-pointer" onClick={() => {
                                    navigate('/account-settings/notification-settings');
                                    closePopover();
                                }} />
                            </Tooltip>
                            <CloseRoundedIcon className="cursor-pointer" onClick={closePopover} />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box ref={scrollRef} className={`py-[10px] px-[15px] h-[350px] overflow-y-auto ${data?.last ? '' : 'pb-[58px]'}`}>
                        {(isFetching || loading) ? <CircularProgress sx={{ color: themeColors?.primary }} size={30} className="absolute left-[47%] top-[47%]" /> : null}
                        <NotificationItem 
                            data={data} 
                            actions={alertActions.actions}
                            types={alertActions.types}
                            handleReadUnread={handleReadUnread}
                            closePopover={closePopover}
                            {...{themeColors, t, pageNo, i18n, loading, navigate}}
                        />
                    </Box>
                </Grid>
                {data?.last ? null : <Grid item xs={12} className='absolute bottom-0 z-10 w-[100%] bg-white'>
                    <Box className='px-[15px] border-t-[#f0f0f0] border-t h-[55px] flex items-center justify-center'>
                        <Button 
                            variant="text"
                            disabled={isFetching}
                            onClick={() => {
                                setTimeout(() => {
                                    scrollRef.current?.scrollTo({
                                        top: scrollRef.current.scrollHeight,
                                        behavior: 'smooth'
                                    });
                                }, 5);
                                setPageNo(pageNo + 1);
                            }}
                            sx={{ boxShadow: 'none', color: isFetching ? '#B8C0C6' : themeColors?.primary, 
                                background: 'none !important',
                                '&:hover': { color: themeColors?.primary + 99, boxShadow: 'none' }, 
                            }}
                            className={`font-Saira font-[600] text-[12px] normal-case`}
                        >{t('viewMore')}</Button>
                    </Box>
                </Grid>}
            </Grid>
        </Popover>
    </>);
}